import { get, isString } from 'lodash';
import { push } from 'redux-first-history';
import { generatePath as generatePathBase, matchPath, resolvePath } from 'react-router';
import { ANALYTICS_MODULE_NAMES } from 'src/analytics/analytics-enums.ts';
import { routes, TRoute } from 'src/views/navigation/routes/routes';
import type { TAppDispatch } from 'store/store-types.d.ts';

export const generatePath = (
  route: TRoute | string,
  params: Record<string, string | number | boolean | undefined>,
  isFull = false,
) => {
  const path = get(route, isFull ? 'fullPath' : 'pattern.path', isString(route) ? route : undefined);
  return generatePathBase(path as string, params);
};

export const isActiveRoute = (route: TRoute | string, url: string) => {
  const path = get(route, 'fullPath', isString(route) ? route : undefined);
  const { pathname } = resolvePath(url, '/');
  return !!matchPath(path as string, pathname);
};

export const navigateToPath = (path: string) => (dispatch: TAppDispatch) => dispatch(push(path));

export const navigateToRoute =
  (route: TRoute | string, params: Record<string, string | number | boolean | undefined>) =>
  (dispatch: TAppDispatch) => {
    const path = generatePath(route, params, true);
    dispatch(push(path));
  };

/**
 * Walks the views defined in this file to find the matching path for analytics module name detection
 */
const walkViewsForAnalyticsModuleNameDetection = (
  node: TRoute | typeof routes,
  normalizedPath: string,
): string | null => {
  if (!node || typeof node !== 'object') {
    return null;
  }
  const isViewNode = (node: TRoute | typeof routes): node is TRoute => 'pattern' in node;

  // Nodes
  if (isViewNode(node)) {
    const paths: string[] = [];
    if (node.fullPath) paths.push(node.fullPath);
    if (node.pattern?.path) paths.push(node.pattern.path);
    for (const path of paths) {
      const match = matchPath(path, normalizedPath);
      if (match && node.analyticsModuleName) {
        return node.analyticsModuleName;
      }
    }
  }

  // Nested nodes
  const nested = (node as TRoute).nested || (node as Record<string, TRoute>);
  for (const key of Object.keys(nested)) {
    const child = nested[key];
    if (typeof child === 'object') {
      const result = walkViewsForAnalyticsModuleNameDetection(child, normalizedPath);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

/**
 * Gets the module name from a URL pathName, for analytics (usage data) in CS reporting
 */
export const getAnalyticsModuleName = (pathName: string) => {
  const { pathname: normalizedPath } = resolvePath(pathName, '/');

  return walkViewsForAnalyticsModuleNameDetection(routes, normalizedPath) ?? ANALYTICS_MODULE_NAMES.OTHER;
};
